import { useEffect, useRef, useState } from "react"
import { standardizeAddress } from "../api/spectrum-address";

/**
 * A modal component that validates and standardizes addresses using the Spectrum Address API.
 * When an address cannot be verified, it presents the user with options to either use their
 * original address or accept a recommended standardized address.
 * 
 * @component
 * @param {Object} props - The component props
 * @param {Object} props.givenAddress - The address object to be validated and standardized
 * @param {string} props.givenAddress.street - The street address
 * @param {string} [props.givenAddress.street2] - Optional second line of street address
 * @param {string} props.givenAddress.city - The city name
 * @param {string} props.givenAddress.state - The state code or name
 * @param {string|Object} props.givenAddress.zipCode - The ZIP code (string or object with zip5 property)
 * @param {Function} [props.onAddressConfirmed] - Callback function called when user confirms address selection
 * @returns {JSX.Element} The StandardizeAddressModal component
 * 
 * @example
 * <StandardizeAddressModal 
 *   givenAddress={{
 *     street: "123 Main St",
 *     city: "Anytown", 
 *     state: "CA",
 *     zipCode: "12345"
 *   }}
 *   onAddressConfirmed={(selectedAddress) => console.log(selectedAddress)}
 * />
 */
export default function StandardizeAddressModal({ givenAddress, onAddressConfirmed = () => { } }) {
    /** @type {[Object|undefined, Function]} Response from the address standardization API */
    const [standardizeAddressResponse, setStandardizeAddressResponse] = useState();

    /** @type {[Object|null, Function]} The recommended standardized address from the API */
    const [recommendedAddress, setRecommendedAddress] = useState();

    /** @type {[boolean, Function]} Controls whether the modal is shown */
    const [showModal, setShowModal] = useState(false);

    /** @type {React.RefObject} The user's selected option: "givenAddress" or "standardizedAddress" */
    const selectedOptionRef = useRef();

    /** @type {React.RefObject} Reference to the radio button group element for event handling */
    const radioButtonGroupRef = useRef();

    /**
     * Sets up event listener for radio button group value changes.
     * Listens for the "value-change" custom event and updates the selected option ref.
     * Includes cleanup to remove stale event listeners.
     */
    useEffect(() => {
        selectedOptionRef.current = "standardizedAddress"

        const handleValueChange = (event) => {
            selectedOptionRef.current = event.detail.value;
        };

        const radioButtonGroup = radioButtonGroupRef.current;
        radioButtonGroup?.addEventListener("value-change", handleValueChange);

        // Cleanup function to remove event listener
        return () => {
            radioButtonGroup?.removeEventListener("value-change", handleValueChange);
            selectedOptionRef.current = "standardizedAddress"
        };
    }, [showModal]);

    /**
     * Initiates address standardization when a complete address is provided.
     * Only processes addresses that have all required fields (street, city, state, zipCode).
     */
    useEffect(() => {
        if (addressIsComplete(givenAddress)) {
            standardizeAddress({ address: givenAddress })
                .then(response => setStandardizeAddressResponse(response));
        }
    }, [givenAddress]);

    /**
     * Processes the standardization response and sets the recommended address.
     * Only sets a recommended address if verification succeeded with high confidence.
     * Normalizes the zipCode format to use zip5 property.
     * Shows the modal when a recommended address is available.
     */
    useEffect(() => {
        if (addressVerificationSucceeded(standardizeAddressResponse)) {
            setRecommendedAddress({
                ...standardizeAddressResponse.addresses[0],
                zipCode: standardizeAddressResponse.addresses[0].zipCode.zip5
            });
            setShowModal(true);
        } else {
            setRecommendedAddress(null);
            setShowModal(false);
        }
    }, [standardizeAddressResponse]);

    /**
     * Handles the Next button click event.
     * Calls the onAddressConfirmed callback with either the original address
     * or the recommended address based on user selection.
     * Hides the modal after confirmation.
     */
    function onNext() {
        onAddressConfirmed(selectedOptionRef.current === "givenAddress" ? givenAddress : recommendedAddress);
        setShowModal(false);
        setRecommendedAddress(null);
    };

    return <>
        {showModal &&
            <gds-modal type="modal" open>
                <span slot="header">Use recommended address instead?</span>
                <div>
                    <p>We’re unable to verify the address you entered, but we found a close match.</p>

                    <gds-radio-button-group ref={radioButtonGroupRef}>
                        <gds-radio-button value="givenAddress">
                            <span slot="label">
                                Use address I entered
                            </span>
                            <p slot="description">
                                <FormattedAddress address={givenAddress} />
                            </p>
                        </gds-radio-button>
                        <gds-radio-button
                            checked={true}
                            value="standardizedAddress"
                        >
                            <span slot="label">
                                Use recommended address
                            </span>
                            <p slot="description">
                                <FormattedAddress address={recommendedAddress} />
                            </p>
                        </gds-radio-button>
                    </gds-radio-button-group>
                </div>

                <gds-button appearance="primary" slot="buttons" onClick={onNext}>Next</gds-button>
            </gds-modal>
        }
    </>
}

/**
 * A component that renders a formatted address display.
 * 
 * @component
 * @param {Object} props - The component props
 * @param {Object} props.address - The address object to format and display
 * @param {string} [props.address.street] - The street address
 * @param {string} [props.address.street2] - Optional second line of street address
 * @param {string} [props.address.city] - The city name
 * @param {string} [props.address.state] - The state code or name
 * @param {string|Object} [props.address.zipCode] - ZIP code (string or object with zip5 property)
 * @returns {JSX.Element} Formatted address display
 */
function FormattedAddress({ address }) {
    const zipCode = address.zipCode?.zip5 ?? address.zipCode;

    return (
        <span>
            {address.street ? <span>{address.street}<br /></span> : <span></span>}
            {address.street2 ? <span>{address.street2}<br /></span> : <span></span>}
            {address.city ? <span>{address.city}, </span> : <span></span>}
            {address.state ? <span>{address.state} </span> : <span></span>}
            {address.zipCode?.zip5 ? <span>{zipCode}</span> : <span></span>}
        </span>
    );
}

/**
 * Validates whether an address object contains all required fields.
 * Checks for the presence of street, city, state, and zipCode properties.
 * 
 * @param {Object} address - The address object to validate
 * @param {string} address.street - The street address
 * @param {string} address.city - The city name
 * @param {string} address.state - The state code or name
 * @param {string|Object} address.zipCode - The ZIP code
 * @returns {boolean} True if all required fields are present and truthy, false otherwise
 */
function addressIsComplete(address) {
    return ["street", "city", "state", "zipCode"].every(key => !!address?.[key]);
}

/**
 * Determines if address verification was successful based on API response criteria.
 * Checks for Success status, 100% confidence level, and excludes S80 match codes.
 * 
 * @param {Object} standardizeAddressResponse - The response from the address standardization API
 * @param {string} standardizeAddressResponse.addressVerificationStatus - Status of verification ("Success", etc.)
 * @param {Array} standardizeAddressResponse.addresses - Array of standardized addresses
 * @param {number} standardizeAddressResponse.addresses[].confidenceLevel - Confidence level (0-100)
 * @param {Object} standardizeAddressResponse.addressVerificationDetails - Additional verification details
 * @param {string} standardizeAddressResponse.addressVerificationDetails.matchCode - Match code from verification
 * @returns {boolean} True if verification succeeded with high confidence, false otherwise
 */
function addressVerificationSucceeded(standardizeAddressResponse) {
    return standardizeAddressResponse?.addressVerificationStatus === "Success" &&
        standardizeAddressResponse.addresses[0].confidenceLevel === 100 &&
        standardizeAddressResponse.addressVerificationDetails.matchCode !== "S80";
}
